import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArticleContent,
  ArticleFigure,
  ArticleImage,
  ArticleLayout,
  ArticleList,
  ArticleParagraph,
  ArticleSection,
  ArticleSidebar,
  ArticleSubtitle,
  SectionNumber,
  SectionTitle,
  TocButton,
  TocLabel,
  TocList,
  TocNumber,
} from "./AboutArticleElements";
import useAboutSections from "./useAboutSections";
import { Reveal } from "../../general/Reveal";
import { useLanguage } from "../../../contexts";
import { scrollBehavior } from "../../../utils";

const formatNumber = (index) => String(index + 1).padStart(2, "0");

const SCROLL_SETTLE_MS = 150;
const MAX_REALIGN_ATTEMPTS = 3;
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown"];

// Lazy images above the target load while the page scrolls past them and push
// the target further down, so a single smooth scroll stops short. Re-align
// once scrolling settles, unless the user takes over scrolling themselves.
const scrollToElement = (element) => {
  let attemptsLeft = MAX_REALIGN_ATTEMPTS;
  let timeout;

  const stop = () => {
    clearTimeout(timeout);
    window.removeEventListener("scroll", handleScroll);
    USER_SCROLL_EVENTS.forEach((event) =>
      window.removeEventListener(event, stop)
    );
  };

  const realignIfNeeded = () => {
    const offset = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
    const drift = element.getBoundingClientRect().top - offset;
    if (Math.abs(drift) <= 2 || attemptsLeft === 0) {
      stop();
      return;
    }
    attemptsLeft -= 1;
    element.scrollIntoView({ behavior: scrollBehavior() });
    timeout = setTimeout(realignIfNeeded, SCROLL_SETTLE_MS);
  };

  function handleScroll() {
    clearTimeout(timeout);
    timeout = setTimeout(realignIfNeeded, SCROLL_SETTLE_MS);
  }

  window.addEventListener("scroll", handleScroll, { passive: true });
  USER_SCROLL_EVENTS.forEach((event) =>
    window.addEventListener(event, stop, { passive: true })
  );
  element.scrollIntoView({ behavior: scrollBehavior() });
  timeout = setTimeout(realignIfNeeded, SCROLL_SETTLE_MS);
};

// Turns the section content into renderable blocks: text is split into
// paragraphs on line breaks, and consecutive "- " lines become one list.
const toBlocks = (content) => {
  const blocks = [];
  content.forEach((item) => {
    if (item.role !== "content") {
      blocks.push(item);
      return;
    }
    item.text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        if (!line.startsWith("- ")) {
          blocks.push({ role: "paragraph", text: line });
          return;
        }
        const previous = blocks[blocks.length - 1];
        if (previous?.role === "list") {
          previous.items.push(line.slice(2));
        } else {
          blocks.push({ role: "list", items: [line.slice(2)] });
        }
      });
  });
  return blocks;
};

const renderBlock = (block, index) => {
  switch (block.role) {
    case "title":
      return (
        <Reveal key={index}>
          <ArticleSubtitle>{block.text}</ArticleSubtitle>
        </Reveal>
      );
    case "paragraph":
      return (
        <Reveal key={index}>
          <ArticleParagraph>{block.text}</ArticleParagraph>
        </Reveal>
      );
    case "list":
      return (
        <Reveal key={index}>
          <ArticleList>
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{item}</li>
            ))}
          </ArticleList>
        </Reveal>
      );
    case "image":
      return (
        <Reveal key={index} from="scale" duration={1}>
          <ArticleFigure>
            <ArticleImage src={block.src} alt={block.alt} loading="lazy" />
          </ArticleFigure>
        </Reveal>
      );
    default:
      return null;
  }
};

const AboutArticle = () => {
  const sections = useAboutSections();
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState(sections[0]?.id);
  const tocRef = useRef(null);

  const sectionBlocks = useMemo(
    () => sections.map((section) => toBlocks(section.content)),
    [sections]
  );

  // Highlight the section that crosses a band just above the middle of the
  // viewport.
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-35% 0px -60% 0px" }
    );
    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [sections]);

  // On narrow screens the contents list scrolls sideways; keep the active
  // entry visible.
  useEffect(() => {
    const toc = tocRef.current;
    if (!toc || toc.scrollWidth <= toc.clientWidth) return;
    const activeItem = toc.querySelector(`[data-section="${activeSection}"]`);
    if (!activeItem) return;
    toc.scrollTo({
      left:
        activeItem.offsetLeft - (toc.clientWidth - activeItem.offsetWidth) / 2,
      behavior: scrollBehavior(),
    });
  }, [activeSection]);

  const handleScrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      scrollToElement(section);
      setActiveSection(id);
    }
  };

  return (
    <ArticleLayout>
      <ArticleSidebar>
        <TocLabel>{t("ABOUT.CONTENTS")}</TocLabel>
        <TocList ref={tocRef}>
          {sections.map((section, index) => {
            const isActive = activeSection === section.id;
            return (
              <li key={section.id} data-section={section.id}>
                <TocButton
                  type="button"
                  $active={isActive}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => handleScrollToSection(section.id)}
                >
                  <TocNumber>{formatNumber(index)}</TocNumber>
                  {section.title}
                </TocButton>
              </li>
            );
          })}
        </TocList>
      </ArticleSidebar>

      <ArticleContent>
        {sections.map((section, index) => (
          <ArticleSection id={section.id} key={section.id}>
            <Reveal>
              <SectionNumber>{formatNumber(index)}</SectionNumber>
              <SectionTitle>{section.title}</SectionTitle>
            </Reveal>
            {sectionBlocks[index].map(renderBlock)}
          </ArticleSection>
        ))}
      </ArticleContent>
    </ArticleLayout>
  );
};

export default AboutArticle;
